import React, { useEffect, useMemo, useState } from 'react';
import styles from './MoveModalContent.module.scss';
import { CustomButton } from '../Button/Button';
import { Icon } from '../Icon';
import cx from 'classnames';
import RadioButton from '../RadioButton/RadioButton';
import CreateFolderModal from '../CreateFolderModal/CreateFolderModal';
import ShareFolderModal from '../ShareFolderModal/ShareFolderModal';
import { useLibraryContext } from '../../context/LibraryContext';
import { useTranslation } from 'react-i18next';
import { FOLDER_USER_ROLE } from '../../constants/UserRoles';
import en from '../../utils/translations/en.json';
import de from '../../utils/translations/de.json';
import nl from '../../utils/translations/nl.json';
import WarningNotification from '../WarningNotification/WarningNotification';
import CloseButton from '../CloseButton/CloseButton';
import useFetchFoldersList from '../../hooks/useFetchFoldersList';
import SearchComponent from '../SearchComponent/SearchComponent';
import RecordNotFound from '../_empty_state/RecordNotFound';
import { MAX_NAME_LENGTH } from '../../../common/constants/common';
import useAccessibleFocus from '../../../common/hooks/useAccessibleFocus';

const MAIN_LIBRARY = 'Videos';
const ARCHIVE_LIBRARY = 'Archive';

const TYPICAL_NAME_VARIANTS = {
  enGB: en['general.untitled_folder'],
  de: de['general.untitled_folder'],
  nl: nl['general.untitled_folder'],
};

function MoveModalContent(props) {
  const { onClose, handleMoving, userId, video, selectedVideos, archived = false, isMobileView } = props;

  const { t } = useTranslation();
  const TYPICAL_NAME = t('general.untitled_folder');

  const [chosenFolder, setChosenFolder] = useState({ id: null, name: MAIN_LIBRARY });
  const [currentVideoFolder, setCurrentVideoFolder] = useState(MAIN_LIBRARY);
  const { updateFolderToMove } = useLibraryContext();
  const [videoName, setVideoName] = useState(video.name);
  const [newFolderName, setNewFolderName] = useState('');
  const [createNewFolder, setCreateNewFolder] = useState(false);
  const [newFolderCreated, setNewFolderCreated] = useState(false);
  const [loadingNewFolderName, setLoadingNewFolderName] = useState(false);
  const [moveProcessing, setMoveProcessing] = useState(false);
  const [warningMoveModal, setWarningMoveModal] = useState(false);
  const [isHandleSelect, setIsHandleSelect] = useState(false); // triggered flag for accessibility

  const {
    list: folders,
    loading,
    createFolderFromModal,
    searchInput: searchInputFolders,
    allFolders,
    sharedFolderWithUsers,
    editFolderUsers,
    deleteFolderUsers,
    changeFolderEditingRight,
    usersOutsideCompany,
    setUsersOutsideCompany,
    isShowShareFolderModal,
    setIsShowShareFolderModal,
  } = useFetchFoldersList({ archived });

  const [searchInput, setSearchValue] = useState('');

  const [label, setLabel] = useState(MAIN_LIBRARY);

  const [privateFolders, setPrivateFolders] = useState([]);
  const [teamFolders, setTeamFolders] = useState([]);
  const [isNamesHover, setIsNamesHover] = useState(false);

  const [isShowPrivateFolders, setIsShowPrivateFolders] = useState(true);
  const [isShowTeamFolders, setIsShowTeamFolders] = useState(true);

  const accessibleMoveRef = useAccessibleFocus(isHandleSelect);

  const moveWarningTitle = useMemo(() => {
    const isMultipleProtectedVideos = Object.values(selectedVideos)?.filter(({ isProtected }) => isProtected)?.length > 1;
    return isMultipleProtectedVideos
      ? t('move_video_modal.warning.protected_video.plural')
      : t('move_video_modal.warning.protected_video');
  }, [selectedVideos]);

  const moveWarningText = useMemo(() => {
    const isMultipleProtectedVideos = Object.values(selectedVideos)?.filter(({ isProtected }) => isProtected)?.length > 1;
    return isMultipleProtectedVideos
      ? t('move_video_modal.warning.protected_video.text.plural')
      : t('move_video_modal.warning.protected_video.text');
  }, [selectedVideos]);

  const handleCloseShareModal = () => {
    setIsShowShareFolderModal(false);
    setUsersOutsideCompany([]);
  };

  const handleChange = value => {
    setSearchValue(value);
    searchInputFolders.onChange(value);
  };

  const togglePrivateFolders = () => {
    setIsShowPrivateFolders(!isShowPrivateFolders);
  };

  const toggleTeamFolders = () => {
    setIsShowTeamFolders(!isShowTeamFolders);
  };

  useEffect(() => {
    if (archived) {
      setLabel(ARCHIVE_LIBRARY);
      setCurrentVideoFolder(ARCHIVE_LIBRARY);
      setChosenFolder(prevState => ({ ...prevState, name: ARCHIVE_LIBRARY }));
    }
  }, [archived]);

  const cancelFolderCreation = () => {
    setNewFolderName('');
    setCreateNewFolder(false);
  };

  const getNumbersFromFields = (array, value) => {
    return array
      .filter(item => {
        console.log('item', item.name, 'value', value);
        return item.name.includes(value);
      })
      .map(item => {
        console.log('item from filtered array', item);
        const result = Number.parseInt(item?.name.slice(value.length, item.length));
        return result ? result : 1;
      })
      .filter(item => item < 1000);
  };

  const genereateNewFolderName = async () => {
    handleChange('');
    setLoadingNewFolderName(true);
    const fullList = allFolders;
    const untitledArray = [];
    for (const key in TYPICAL_NAME_VARIANTS) {
      const filteredArray = getNumbersFromFields(fullList, TYPICAL_NAME_VARIANTS[key]);
      untitledArray.push(...filteredArray);
    }
    const sortedArray = untitledArray.sort((a, b) => a - b);
    setNewFolderName(`${TYPICAL_NAME}${sortedArray.length > 0 ? ' ' + (sortedArray[untitledArray.length - 1] + 1) : ''}`);
    setCreateNewFolder(true);
    setLoadingNewFolderName(false);
  };

  useEffect(() => {
    updateFolderToMove(chosenFolder.id, chosenFolder.name);
  }, [chosenFolder]);

  useEffect(() => {
    if (video.id) {
      const folderInfo = allFolders.find(item => item.id === video.folder);
      folderInfo && setCurrentVideoFolder(folderInfo.name);

      if (video.folder) {
        const folderName = allFolders.find(item => item.id === video.folder)?.name;
        setChosenFolder({ id: video.folder, name: folderName });
      } else {
        setChosenFolder({ id: null, name: archived ? ARCHIVE_LIBRARY : MAIN_LIBRARY });
      }
    }

    if (Object.keys(selectedVideos).length) {
      // We can take any video from selectedVideos and check the folder
      // because all selected videos can only be selected in the same folder
      const firstSelectedVideo = Object.values(selectedVideos)[0];
      const folderInfo = allFolders.find(item => item.id === firstSelectedVideo.folder);
      folderInfo && setCurrentVideoFolder(folderInfo.name);

      if (firstSelectedVideo.folder) {
        const folderName = allFolders.find(item => item.id === firstSelectedVideo.folder)?.name;
        setChosenFolder({ id: firstSelectedVideo.folder, name: folderName });
      }
    }

    setIsShowPrivateFolders(true);
    setIsShowTeamFolders(true);
  }, [allFolders]);

  const handleChangeCheckbox = event => {
    const folderId = event.target.id;
    if (folderId !== 'null') {
      const folderInfo = allFolders.find(item => item.id === folderId);
      folderInfo && setChosenFolder({ id: folderId, name: folderInfo.name });
    } else setChosenFolder({ id: null, name: label });

    setIsHandleSelect(true);
    setIsHandleSelect(false);
  };

  useEffect(() => {
    if (newFolderCreated) {
      const newFolder = allFolders.find(folder => folder.name === newFolderName);
      newFolder && setChosenFolder({ id: newFolder.id, name: newFolder.name });
      setCreateNewFolder(false);
      setNewFolderName(t('general.untitled_folder'));
      setNewFolderCreated(false);
    }
  }, [allFolders, newFolderCreated]);

  useEffect(() => {
    // To generate the name of a new folder,
    // we need all the names (archived and non-archived)
    // Therefore, the filter by archive is here,
    // and not inside the hook where folders are stored
    const filteredFoldersByArchive = folders.filter(item => Boolean(item.archived) === archived);

    const tempPrivateFolders = filteredFoldersByArchive?.filter(({ users }) => !users?.length);
    setPrivateFolders(tempPrivateFolders);

    const tempTeamFoldersWithRights = filteredFoldersByArchive?.filter(folder => {
      const isFolderOwner = userId === folder?.owner?.username;
      const isEditor = folder?.users?.find(
        ({ username, folderRole }) => username === userId && folderRole === FOLDER_USER_ROLE.EDITOR.TYPE
      );

      return folder?.users?.length && (isFolderOwner || isEditor);
    });
    setTeamFolders(tempTeamFoldersWithRights);
  }, [folders]);

  useEffect(() => {
    return () => {
      setChosenFolder({ id: null, name: label });
    };
  }, []);

  const handleFolderCreation = async name => {
    setNewFolderName(name);
    await createFolderFromModal(name);
    setNewFolderCreated(true);
    setIsShowShareFolderModal(true);
  };

  const checkPassword = async () => {
    if (Object.values(selectedVideos).length >= 1) {
      const checkVideosProtected = Object.values(selectedVideos).map(item => item.isProtected);
      if (checkVideosProtected.find(item => Boolean(item))) {
        setWarningMoveModal(true);
      } else {
        moveToFolder();
      }
    } else {
      if (video.isProtected) {
        setWarningMoveModal(true);
      } else {
        moveToFolder();
      }
    }
  };

  const moveToFolder = async () => {
    setMoveProcessing(true);
    handleMoving();
  };

  const handleNamesMouseEnter = () => {
    setIsNamesHover(true);
  };

  const handleNamesMouseLeave = () => {
    setIsNamesHover(false);
  };

  return (
    <>
      <div className={styles.modalTitle}>
        <div className={styles.header}>
          <div className={styles.namesContainer} onMouseEnter={handleNamesMouseEnter} onMouseLeave={handleNamesMouseLeave}>
            {videoName ? (
              <h1 tabIndex={0}>
                {t('general.video_action_button.move')} {videoName}
              </h1>
            ) : (
              <h1 tabIndex={0}>
                {t('general.video_action_button.move')}{' '}
                {Object.values(selectedVideos)
                  .map(({ name }) => name)
                  .join(', ')}
              </h1>
            )}
            {isNamesHover ? (
              <div className={styles.fullList}>
                {videoName ? <span tabIndex={0} className={styles.listItem}>{videoName}</span> : Object.values(selectedVideos).map(({ name }) => <span tabIndex={0} className={styles.listItem}>{name}</span>)}
              </div>
            ) : null}
          </div>
          <CloseButton onClose={onClose} />
        </div>
        <h2 tabIndex={0} className={styles.subtitle}>{t('general.video_action_button.move.currently_in')}</h2>
        <p tabIndex={0} className={styles.currentFolder}>{currentVideoFolder}</p>
      </div>
      <div className={styles.foldersContainer}>
        <div className={styles.searchContainer}>
          <h2 tabIndex={0}>{t('general.video_action_button.move.move_to')}</h2>
          <SearchComponent searchValue={searchInput} onChange={handleChange} clearButton={!isMobileView} maxLength={MAX_NAME_LENGTH} />
        </div>
        <div className={styles.listContainer}>
          <ul className={styles.foldersList}>
            <li key="Mylibrary">
              <RadioButton
                id="null"
                checked={chosenFolder.id === null}
                onChange={handleChangeCheckbox}
                label={label}
                variant={'secondary'}
                className={styles.listItem}
                disabled={createNewFolder}
                isSelectByTitle={true}
              />
            </li>
            {loading ? (
              <li className={styles.loading}>{t('general.loading')}</li>
            ) : (
              <>
                {!!privateFolders.length && (
                  <li className={styles.foldersTitle} onClick={togglePrivateFolders}>
                    <span>{t('dashboard.folders.title')}</span>
                    <div className={cx(styles.dropdownBox)}>
                      <Icon className={cx(styles.arrowIcon, { [styles.isActive]: isShowPrivateFolders })} src="arrowDown" />
                    </div>
                  </li>
                )}
                {isShowPrivateFolders &&
                  !!privateFolders.length &&
                  privateFolders.map(folder => (
                    <li key={folder.id}>
                      <RadioButton
                        id={folder.id}
                        checked={chosenFolder.id === folder.id}
                        onChange={handleChangeCheckbox}
                        additionalLabel={`${t('dashboard.folders.title')} /`}
                        label={folder.name}
                        variant={'secondary'}
                        className={styles.listItem}
                        disabled={createNewFolder}
                        isHighlight
                        searchInput={searchInput?.trim()}
                        isSelectByTitle={true}
                      />
                    </li>
                  ))}
                {!!teamFolders.length && (
                  <li className={styles.foldersTitle} onClick={toggleTeamFolders}>
                    <span>{t('dashboard.team_folders.title')}</span>
                    <div className={cx(styles.dropdownBox)}>
                      <Icon className={cx(styles.arrowIcon, { [styles.isActive]: isShowTeamFolders })} src="arrowDown" />
                    </div>
                  </li>
                )}
                {isShowTeamFolders &&
                  !!teamFolders.length &&
                  teamFolders.map(folder => (
                    <li key={folder.id}>
                      <RadioButton
                        id={folder.id}
                        checked={chosenFolder.id === folder.id}
                        onChange={handleChangeCheckbox}
                        additionalLabel={`${t('dashboard.team_folders.title')} /`}
                        label={folder.name}
                        variant={'secondary'}
                        className={styles.listItem}
                        disabled={createNewFolder}
                        isHighlight
                        searchInput={searchInput?.trim()}
                        isSelectByTitle={true}
                      />
                    </li>
                  ))}
              </>
            )}
          </ul>
          {searchInput && !loading && !teamFolders.length && !privateFolders.length && (
            <RecordNotFound searchTerm={searchInput} className={styles.notFound} />
          )}
        </div>
        <CustomButton
          variant="secondary"
          label={
            <>
              <Icon src="addFolder" className={styles.addFolderIcon} />
              <span>{t('general.add')}</span>
            </>
          }
          className={styles.addFolderBtn}
          onClick={genereateNewFolderName}
          loading={loadingNewFolderName}
          disabled={loading}
          loadingText={
            <>
              <Icon src="addFolder" className={styles.addFolderIcon} />
              <span>{t('general.add')}...</span>
            </>
          }
        />
      </div>
      <div className={styles.modalActionsContainer} tabIndex={0} ref={accessibleMoveRef}>
        <CustomButton variant="link" label={t('general.cancel')} className={styles.cancelButton} onClick={onClose} />
        <CustomButton
          variant="primary"
          label={t('general.video_action_button.move')}
          onClick={checkPassword}
          disabled={chosenFolder.name === currentVideoFolder || createNewFolder || moveProcessing}
        />
      </div>
      <CreateFolderModal
        isShow={createNewFolder}
        defaultName={newFolderName}
        onClose={cancelFolderCreation}
        createFolder={handleFolderCreation}
      />
      {isShowShareFolderModal && (
        <ShareFolderModal
          sharedFolderWithUsers={sharedFolderWithUsers}
          editFolderUsers={editFolderUsers}
          deleteFolderUsers={deleteFolderUsers}
          changeFolderEditingRight={changeFolderEditingRight}
          onClose={handleCloseShareModal}
          isShow={isShowShareFolderModal}
          folder={allFolders[0]}
          usersOutsideCompany={usersOutsideCompany}
        />
      )}
      <WarningNotification
        open={warningMoveModal}
        onClose={() => setWarningMoveModal(false)}
        onSubmit={moveToFolder}
        title={moveWarningTitle}
        text={moveWarningText}
        btnText={t('move_video_modal.warning.protected_video')}
      />
    </>
  );
}

export default MoveModalContent;
